import { inject } from '@angular/core';
import { getState, patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { deepmerge } from 'deepmerge-ts';

import { UpdateUserSettingsPayload, UserSettings, userSettingsSchema } from '@optical/types';

import { UserSettingsService } from '../../services';
import { setError, setFulfilled, setPending, withRequestStatus } from '../request-status.feature';

// Parsing `{}` creates a default user settings object with all fields set to their `catch` values.
const initialState: UserSettings = userSettingsSchema.parse({});

const UserSettingsActions = {
  loadUserSettings: 'loadUserSettings',
  updateUserSettings: 'updateUserSettings',
} as const;

export type UserSettingsStore = InstanceType<typeof UserSettingsStore>;

export const UserSettingsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus({ action: UserSettingsActions.loadUserSettings }),
  withRequestStatus({ action: UserSettingsActions.updateUserSettings }),
  withMethods((store, userSettingsService = inject(UserSettingsService)) => ({
    async loadUserSettings(): Promise<UserSettings> {
      patchState(store, setPending({ action: UserSettingsActions.loadUserSettings }));

      try {
        const userSettings = await userSettingsService.getUserSettings();

        patchState(
          store,
          userSettings,
          setFulfilled({ action: UserSettingsActions.loadUserSettings }),
        );

        return userSettings;
      } catch (err) {
        patchState(store, setError(err, { action: UserSettingsActions.loadUserSettings }));
        console.error(err);

        throw err;
      }
    },
    async updateUserSettings(payload: UpdateUserSettingsPayload): Promise<UserSettings> {
      const userSettings = getState(store);
      const optimisticUserSettings = deepmerge(userSettings, payload);

      patchState(
        store,
        setPending({ action: UserSettingsActions.updateUserSettings }),
        // Optimistically update the user settings in the store
        optimisticUserSettings,
      );

      try {
        const userSettings = await userSettingsService.updateUserSettings(payload);

        patchState(store, setFulfilled({ action: UserSettingsActions.updateUserSettings }));

        return userSettings;
      } catch (err) {
        patchState(
          store,
          setError(err, { action: UserSettingsActions.updateUserSettings }),
          // Revert optimistic update on error
          userSettings,
        );

        console.error(err);

        throw err;
      }
    },
    resetUserSettings(): void {
      patchState(store, initialState);
    },
  })),
);
